<template>
  <div id="app">
    <SemcogHeader>
      <span
       style="line-height: 2em; font-size: 1em;"
      >
       Broadband Availability and Usage
      </span>
    </SemcogHeader>
    <Loader v-if="downloadingData > 0"/>
    <AvalibilityProfile
      v-bind:allGeogData="filteredGeogData"
      v-bind:regionData="regionData"
      v-bind:selectedId="selectedId"
      v-bind:selectedName="selectedName"
      v-bind:geotype="geotype"
      v-bind:namesFromGeotype="namesFromGeotype"
    />
    <MainMap
      v-bind:geotype="geotype"
      v-bind:allGeojsonByType="allGeojsonByType"
      v-bind:getIndMoeName="getIndMoeName"
      v-bind:namesFromGeotype="namesFromGeotype"
      v-bind:namesForSelectedType="namesForSelectedType"
      v-bind:selectedId="selectedId"
      v-bind:hoverId="printing ? null: hoverId"
      v-on:set-sel-geom-id="selectedId = $event"
      v-on:set-hov-geom-id="hoverId = $event"
      v-on:set-geo-type="setGeoType($event)"
    />
    <UsageProfile
      v-bind:allGeogData="filteredGeogData"
      v-bind:regionData="regionData"
      v-bind:selectedId="selectedId"
      v-bind:selectedName="selectedName"
      v-bind:geotype="geotype"
      v-bind:namesFromGeotype="namesFromGeotype"
    />
  </div>
</template>

<script>
import { diff } from 'deep-diff';
import * as turf from 'turf'

import UsageProfile from './components/UsageProfile.vue'
import AvalibilityProfile from './components/AvalibilityProfile.vue'
import MainMap from './components/MainMap.vue'
import SemcogHeader from './components/SemcogHeader.vue'

import { featureCollection } from 'turf'
import Loader from './components/map/Loader'

export default {
  name: 'app',
  components: { Loader, MainMap, UsageProfile, AvalibilityProfile, SemcogHeader },
  data: function () {
    return {
      downloadingData: 0,
      printing: false,
      selectedId: parseInt(this.$route.query.community) || null,
      hoverId: null,
      geotype: 'mcd',
      geotypes: Object.freeze(['region', 'county', 'mcd','SchoolDistrict', 'CensusTract', 'BlockGroup', 'ISD']),
      namesFromGeotype: Object.freeze({
        'county': {
          name: 'Counties',
          singularName: 'County',
        },
        'mcd': {
          name: 'Communities',
          singularName: 'Community',
        },
        'SchoolDistrict': {
          name: 'School Districts',
          singularName: 'School District',
        },
        'ISD': {
          name: 'Intermediate School Districts',
          singularName: 'Intermediate School District',
        },
        'CensusTract': {
          name: 'Census Tracts',
          singularName: 'Census Tract',
        },
        'BlockGroup': {
          name: 'Block Groups',
          singularName: 'Block Group',
        },
      }),
      allGeojsonByType: Object.freeze(new Map()),
    }
  },
  mounted: function () {
    const mql = window.matchMedia('print')
    const cb = ({ matches }) => {
      this.printing = matches
    }
    // IOS dose not  work with addEventListener
    mql.addListener(cb)
    cb(mql)
  },
  computed: {
    geojsonFile: function () {
      return './geojson/boundaries.geojson'
    },
    nameFromId: function () {
      let nameFromId = new Map()
      this.geotypes.forEach(type => {
        nameFromId.set(type, new Map((this.allGeogData.get(type) || []).map(val => [val.geoid, [(val.name || '').trim(), val.county]])))
      })
      return nameFromId
    },
    namesForSelectedType: function () {
      if (!this.nameFromId.get(this.geotype)) {
        return new Map()
      }
      return this.nameFromId.get(this.geotype)
    },
    filteredGeogData: function () {
      if (!this.allGeogData.get(this.geotype)) {
        return []
      }
      return this.allGeogData.get(this.geotype)
    },
    selectedName: function () {
      let out = this.namesForSelectedType.get(this.selectedId)
      if (!out) {
        return
      }
      return out[0]
    },
    regionData: function () {
      let out = this.allGeogData.get('region')
      if (!out) {
        return {}
      }
      return out.find(r => r.geoid === 8999)
    },
    columnsByGeoType: function () {
      let columnsByGeoType = new Map()
      this.geotypes.forEach(type => {
        let g = new Set();
        (this.allGeogData.get(type) || []).forEach(f =>
          Object.keys(f).forEach(c => {
              if (f[c] && !isNaN(f[c])) {
                g.add(c)
              }
            },
          ),
        )
        columnsByGeoType.set(type, g)
      })
      return columnsByGeoType
    },
    allGeogData: function () {
      let allGeogData = new Map()
      this.geotypes.forEach(type => {
        let json = this.allGeojsonByType.get(type)
        if (json && json.features) {
          allGeogData.set(type, json.features.map(f => f.properties))
        }
      })
      return allGeogData
    },
    urlData: function () {
      if (this.downloadingData) {
        // none of our computed are right without data so just leave the url the same
        return this.$route.query
      }
      let query = {};
      if (this.geotype === 'mcd' && this.selectedId && this.selectedId !== 8999) {
        query.community = this.selectedId.toString()
      }
      return query;
    },
  },
  methods: {
    getIndMoeName: function (ind) {
      let columnsInGeojson = this.columnsByGeoType.get(this.geotype)
      let moe = undefined
      if (!columnsInGeojson || !columnsInGeojson.has) {
        return moe
      }
      if (columnsInGeojson.has('m_' + ind)) {
        moe = 'm_' + ind
      }
      return moe
    },
    setGeoType: function (id) {
      this.geotype = id
      this.hoverId = null
      this.selectedId = null
    },
  },
  watch: {
    geojsonFile: {
      immediate: true,
      handler:
        function () {
          this.downloadingData += 1
          fetch(this.geojsonFile).then(r => r.json()).then(data => {
            if (data.description) {
              let names = data.description.split(',')
              data.features.forEach(f => {
                let out = {}
                Object.keys(f.properties).forEach(p => {
                  out[names[parseInt(p, 16)]] = f.properties[p]
                })
                f.properties = out
              })
            }
            let offset = data.features.find(f => f.properties.geoid === 8999)
            if (offset) {
              offset = offset.geometry && offset.geometry.coordinates
              if (offset.length && offset.length === 2) {
                let offset_x = offset[0]
                let offset_y = offset[1]
                turf.coordEach(data, pointCoords => {
                  pointCoords[0] -= offset_x
                  pointCoords[1] -= offset_y
                })
              }
            }
            const geotypes = new Set(data.features.map(row => row.properties.geotype))

            let allGeojsonByType = new Map()
            geotypes.forEach(type => {
              allGeojsonByType.set(type, featureCollection(data.features.filter(row => row.properties.geotype === type)))
            })
            this.allGeojsonByType = Object.freeze(allGeojsonByType)
            this.$nextTick(() => {
              this.downloadingData -= 1
            })
          });
        },
    },
    urlData: function (oldUrl, newUrl) {
      if (diff(oldUrl, newUrl)) {
         this.$router.replace({ query: this.urlData })
      }
    },
  },
}

</script>

<style scoped>
  #app {
    width: 100%;
    height: 100vh;
    display: grid;
    grid-template-columns: .65fr 1fr 1fr;
    grid-template-rows: max-content auto;
    grid-gap: 3px;
    font-family: Arial, Helvetica, sans-serif;
  }

  @media (max-width: 1024px) {
    #app {
      height: initial;
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 576px), print {
    #app {
      height: initial;
      grid-template-columns: auto;
      grid-template-rows: max-content max-content auto;
    }
  }
</style>
